import {Box, Group, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {getToolDisplayName} from 'llm/deep-research/tool-details/types';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK, NEAR_BLACK, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface ToolInfo {
  name: string;
  description: string | null;
  parameters: unknown;
}
interface McpToolListProps {
  tools: Array<ToolInfo>;
  error: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
  renderAccessControl: (tool: ToolInfo) => JSX.Element;
  expandable?: boolean;
}

export default function McpToolList(props: McpToolListProps): JSX.Element {
  const {tools, error, onRetry, isRetrying, renderAccessControl, expandable = false} = props;
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const toggleToolExpanded = (toolName: string): void => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  };

  if (error) {
    return (
      <div className={css(styles.errorState)}>
        <BenchlingIcon icon="warning" className={css(styles.errorIcon)} />
        <Text className={css(styles.errorText)}>{error}</Text>
        {onRetry && (
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={onRetry}
            disabled={isRetrying}
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
        )}
      </div>
    );
  }

  if (tools.length === 0) {
    return (
      <div className={css(styles.emptyState)}>
        <Text className={css(styles.emptyText)}>No tools available</Text>
      </div>
    );
  }

  return (
    <div className={css(styles.toolTable)}>
      <div className={css(styles.tableHeader)}>
        <Text className={css(styles.tableHeaderLabel, styles.toolColumn)}>Tool</Text>
        <Text className={css(styles.tableHeaderLabel, styles.accessColumn)}>Access</Text>
      </div>
      <div className={css(styles.tableBody)}>
        {tools.map((tool) => {
          const isExpanded = expandable && expandedTools.has(tool.name);

          return (
            <div key={tool.name} className={css(styles.toolRowContainer)}>
              <div className={css(styles.toolRow)}>
                <div
                  className={css(styles.toolInfo, styles.toolColumn, expandable && styles.clickable)}
                  onClick={
                    expandable
                      ? () => {
                          toggleToolExpanded(tool.name);
                        }
                      : undefined
                  }
                >
                  <Group spacing={8} noWrap={true} align="flex-start">
                    {expandable && (
                      <BenchlingIcon
                        icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                        className={css(styles.expandIcon)}
                      />
                    )}
                    <div className={css(styles.toolInfoContent)}>
                      <Text className={css(styles.toolName)}>{getToolDisplayName(tool.name)}</Text>
                      {tool.description && !isExpanded && (
                        <Text className={css(styles.toolDescription)} lineClamp={1}>
                          {tool.description}
                        </Text>
                      )}
                    </div>
                  </Group>
                </div>
                <Box className={css(styles.selectWrapper, styles.accessColumn)}>
                  {renderAccessControl(tool)}
                </Box>
              </div>
              {isExpanded && tool.description && (
                <div className={css(styles.toolDetails)}>
                  <Text className={css(styles.detailLabel)}>Description</Text>
                  <Text className={css(styles.detailText)}>{tool.description}</Text>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  toolTable: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px 8px 0',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
    gap: 16,
  },
  tableHeaderLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: GRAY_SEMIDARK,
    textTransform: 'uppercase',
  },
  tableBody: {
    flex: 1,
    overflowY: 'auto',
  },
  toolColumn: {
    flex: 1,
    minWidth: 0,
  },
  accessColumn: {
    width: 180,
    flexShrink: 0,
  },
  toolRowContainer: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  toolRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px 12px 0',
    gap: 16,
  },
  toolInfo: {
    minWidth: 0,
  },
  clickable: {
    cursor: 'pointer',
    ':hover': {
      opacity: 0.8,
    },
  },
  toolInfoContent: {
    minWidth: 0,
    flex: 1,
  },
  expandIcon: {
    color: GRAY_SEMIDARK,
    fontSize: 12,
    marginTop: 2,
    flexShrink: 0,
  },
  toolName: {
    fontSize: 13,
    fontWeight: 600,
    color: NEAR_BLACK,
  },
  toolDescription: {
    fontSize: 12,
    color: GRAY_SEMIDARK,
    marginTop: 2,
  },
  selectWrapper: {},
  toolDetails: {
    marginLeft: 20,
    marginRight: 12,
    marginBottom: 12,
    padding: 12,
    backgroundColor: GRAY_LIGHTER,
    borderRadius: 6,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: GRAY_SEMIDARK,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  detailText: {
    fontSize: 13,
    color: NEAR_BLACK,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 13,
    color: GRAY_SEMIDARK,
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 40,
  },
  errorIcon: {
    color: RED,
    fontSize: 24,
  },
  errorText: {
    fontSize: 13,
    color: GRAY_SEMIDARK,
    textAlign: 'center',
  },
});
